import React, { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import cs from "classnames";
import debounce from "lodash/debounce";

import { useDirectorySearch } from "../../../../../lib/hooks/useDirectorySearch";

import { Person } from "../../../../../api/grpc/contacts/contacts";
import { SearchUserCustomOption } from "../../../../shared/Forms/SearchUserInput/SearchUserCustomOption";
import { DirectoryMenuList } from "../../../../shared/DirectoryMenuList/DirectoryMenuList";
import { CustomNoOptionsMessage } from "../../../../shared/NoOptionsMessage/NoOptionsMessage";

type OptionType = {
  value: Person;
  label: string;
};

interface Props {
  onChange: (values: Person[]) => void;
  attendees: Person[];
  isAddPeople?: boolean;
}

export const FindPeopleSelector = ({
  onChange,
  attendees,
  isAddPeople,
}: Props) => {
  const {
    searching,
    searchError,
    searchResults,
    setSearchResults,
    handleDirectorySearch,
    handleIntegrationSearch,
  } = useDirectorySearch();

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query && query.length > 1) {
        handleDirectorySearch(query);
      }
    }, 500),
    [handleDirectorySearch]
  );

  const handleInputChange = (inputValue: string) => {
    if (inputValue && inputValue.length > 1) {
      debouncedSearch(inputValue);
    }
  };

  const handleManualDirectorySearch = (query: string) => {
    if (query) {
      handleIntegrationSearch(query);
    }
  };

  const options = useMemo(() => {
    if (!searchResults?.people?.length) return [];

    return searchResults.people.map((item) => ({
      value: item,
      label: item.displayName,
    }));
  }, [searchResults]);

  const selectedValue = useMemo(() => {
    return attendees.map((attendee) => ({
      value: attendee,
      label: attendee.displayName,
    })) as OptionType[];
  }, [attendees]);

  return (
    <>
      <h6 className="pb-2">{isAddPeople ? "Add" : "Find"} people</h6>
      {searchError ? (
        <div>Error fetching people, please try again.</div>
      ) : (
        <Select<OptionType, true>
          isClearable
          isMulti
          options={options}
          value={selectedValue}
          classNamePrefix={selectedValue.length >= 2 ? "MultipleVal" : ""}
          noOptionsMessage={() => "No result found"}
          onInputChange={handleInputChange}
          onMenuClose={() => {
            if (searchResults?.people?.length) {
              setSearchResults(undefined);
            }
          }}
          components={{
            MenuList: (props) => (
              <DirectoryMenuList
                {...props}
                searching={searching}
                handleDirectorySearch={handleManualDirectorySearch}
                showNarrowOption={
                  searchResults?.people && searchResults?.people.length > 8
                }
              />
            ),
            Option: SearchUserCustomOption as any,
            NoOptionsMessage: (props) => (
              <CustomNoOptionsMessage {...props} hideMessage={!!searching} />
            ),
          }}
          onChange={(selected) => {
            if (!selected) {
              return;
            }
            const values = (
              Array.isArray(selected) ? selected : [selected]
            ).map((item) => item.value);
            onChange(values);
          }}
          isSearchable={true}
          className={cs(
            "FindPeople FindAllPeople PeopleSelect no-min-height NoOption"
          )}
        />
      )}
    </>
  );
};
